/**
 * Accessory quantities in the cart recommendations.
 *
 * The groups themselves are Liquid - which accessories, in which order, under
 * which heading - but the figures are not: a perimeter needs a square root and
 * Liquid has no filter for one. So the markup ships with the basket's flooring
 * area on it and this fills in the rest.
 *
 * It also owns refreshing the markup. The modal lives in a <template> rendered
 * at page load, so a shopper adding flooring would otherwise open a modal built
 * for the basket they had before the add. Every other refresh path in the modal
 * is built around the Shopify recommendations API and stands down for curated
 * markup, so rather than bend those, this re-fetches the section itself and
 * swaps the group markup - into the live modal and into the template, so the
 * next open is correct too.
 */
import { deriveFromArea, money, quantityFor, renderCard } from "@theme/accessory-quantity";

const GROUPS = "[data-accessory-groups]";
const MODAL = '[data-modal="cart-recommendations"]';
const CONTAINER = ".cart-recommendations";
// Every slider this section can render, in any mode - the accessory groups,
// the curated blocks and the empty-cart fallback alike. Scoping this to the
// accessory groups meant that emptying the basket swapped in markup nothing
// would ever initialise, so the fallback arrived as a column of full-width
// cards with dead arrows.
const SLIDERS = `${CONTAINER} [data-blaze-slider]`;

/** 2.4 rather than 2.40, and 32 rather than 32.0. */
const trim = (value) => String(parseFloat(value.toFixed(1)));

function fillGroups(root) {
  root.querySelectorAll(GROUPS).forEach((groups) => {
    const values = deriveFromArea(parseFloat(groups.dataset.cartArea) || 0, {
      wastagePct: parseFloat(groups.dataset.wastage) || 0,
      perimeterFactor: parseFloat(groups.dataset.perimeterFactor) || 4.2,
      packs: parseInt(groups.dataset.cartPacks, 10) || 0,
    });

    groups.querySelectorAll("[data-accessory-estimate]").forEach((estimate) => {
      const total =
        estimate.dataset.accessoryMethod === "perimeter" ? values.perimeter : values.area;
      const target = estimate.querySelector("[data-estimate-text]");
      if (!target || !(total > 0)) return;
      target.textContent = (estimate.dataset.estimateTemplate || "").replace(
        "[amount]",
        trim(total),
      );
    });

    // A suggestion, not a ruling. Once the shopper has set a quantity by hand
    // it is left alone - this used to run on every click in the page, which
    // meant stepping a card up snapped it straight back a moment later.
    //
    // The signature guards the rest: a card is only written once per set of
    // measurements, so re-running this after a mount or a stray click is a
    // no-op rather than a reset.
    const signature = `${values.area}|${values.perimeter}|${values.packs}`;

    groups.querySelectorAll(".product-card__essentials").forEach((card) => {
      if (!card.dataset.accessoryMethod) return;
      if (card.dataset.accessoryUserSet === "true") return;
      if (card.dataset.accessoryFilledFor === signature) return;

      const input = card.querySelector('input[name="quantity"]');
      if (!input) return;

      input.value = String(quantityFor(card, values));
      card.dataset.accessoryFilledFor = signature;
      renderCard(card);
    });
  });

  renderAddAll();
}

/**
 * Every accessory that can actually be bought, at whatever quantity is on show.
 * A sold-out card has no quick-add button, so it drops out here rather than
 * being sent to the cart to fail.
 */
function collectItems(groups) {
  return [...groups.querySelectorAll(".product-card__essentials")]
    .map((card) => {
      const button = card.querySelector("[quick-add-qf][data-id]");
      const input = card.querySelector('input[name="quantity"]');
      if (!button || !input) return null;

      return {
        id: button.dataset.id,
        quantity: Math.max(1, parseInt(input.value, 10) || 1),
        price: parseFloat(card.dataset.accessoryPrice) || 0,
      };
    })
    .filter(Boolean);
}

/**
 * The bar sits outside the groups - it is a sibling of the scrolling body, so
 * that it stays pinned while they scroll - so it looks for them through the
 * panel it shares with them rather than upwards from itself.
 */
function groupsFor(node) {
  const panel = node.closest("[data-cart-recs-panel]");
  return panel?.querySelector(GROUPS) || document.querySelector(GROUPS);
}

function renderAddAll() {
  document.querySelectorAll("[data-accessory-add-all]").forEach((bar) => {
    const target = bar.querySelector("[data-accessory-add-all-total]");
    const groups = groupsFor(bar);
    if (!target || !groups) return;

    const total = collectItems(groups).reduce(
      (sum, item) => sum + (item.price * item.quantity) / 100,
      0,
    );
    target.textContent = money(total);
  });
}

/** One request for the lot, through the theme's cart so the drawer follows. */
async function addAll(button) {
  const groups = groupsFor(button);
  if (!groups) return;

  const items = collectItems(groups).map(({ id, quantity }) => ({ id, quantity }));
  if (!items.length) return;

  button.disabled = true;
  button.classList.add("is-loading");

  let added = false;
  try {
    if (window.cart?.add) {
      await window.cart.add({ items });
    } else {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: items.map((item) => ({ ...item, id: Number(item.id) })) }),
      });
      if (!response.ok) throw new Error(await response.text());
      document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    }
    added = true;
  } catch (error) {
    console.warn("cart-accessories: add all failed", error);
  } finally {
    button.disabled = false;
    button.classList.remove("is-loading");
  }

  // Only on success, and only once the cart has actually been told. The modal
  // covers the drawer, so leaving it up meant the shopper watched a button go
  // grey and come back with nothing else to show for it - closing hands them
  // straight to the cart, where the additions are visible.
  if (added) closeModal();
}

/**
 * Through the modal's own controller where it has loaded, and through its close
 * button otherwise. The button is bound to the modal controller, so the
 * fallback still goes out the front door rather than hiding the element behind
 * the controller's back and leaving its state saying the modal is open.
 */
function closeModal() {
  if (window.cartRecommendationsModal?.close) {
    window.cartRecommendationsModal.close();
    return;
  }

  document.querySelector(`${MODAL} [data-modal-close]`)?.click();
}

/**
 * Adding a single product from the modal closes it too, so the shopper is
 * handed back to the cart and can see the line arrive. The quick-add owns its
 * own request and resolves nowhere this can reach, so the click is remembered
 * and the next cart event is taken as the confirmation.
 *
 * Not in accessories mode: those cards are a shopping list to work down, and
 * the bar at the foot adds the lot in one go. Closing after each one would put
 * the modal away mid-task.
 */
let closeAfterAdd = null;

function modalMode() {
  const live = document.querySelector(`${MODAL} ${CONTAINER}`);
  return live?.querySelector("[data-accessory-mode]")?.dataset.accessoryMode || "";
}

function onQuickAddClick(event) {
  const button = event.target.closest?.(`${MODAL} [quick-add-qf]`);
  if (!button || button.disabled) return;

  const mode = modalMode();
  if (mode !== "fallback" && mode !== "api") return;

  // Dropped if no cart event follows, so a failed add cannot close the modal
  // out of nowhere the next time the cart happens to change.
  window.clearTimeout(closeAfterAdd);
  closeAfterAdd = window.setTimeout(() => {
    closeAfterAdd = null;
  }, 5000);
}

function onCartChanged() {
  if (closeAfterAdd === null) return;

  window.clearTimeout(closeAfterAdd);
  closeAfterAdd = null;
  closeModal();
}

/**
 * Only a shopper's own change marks a card. fillGroups writes the input
 * directly and never dispatches, so its own writes cannot land here - which is
 * what keeps a suggestion from being mistaken for a choice.
 */
function onQuantityChanged(event) {
  const card = event.target.closest?.(`${GROUPS} .product-card__essentials`);
  if (!card) return;

  card.dataset.accessoryUserSet = "true";
  // The PDP's own listener redraws the card, but it stands down without the
  // calculator element - which is only on the PDP. Here the card is redrawn
  // from the same helper, so a stepped perimeter card shows its new metres.
  renderCard(card);
  renderAddAll();
}

/**
 * The Shopify recommendations hooks are attributes on the container element
 * itself, and Liquid only emits them when the render resolved to the api mode.
 * refresh() swaps the container's innerHTML, which never carried them - so a
 * basket that moved from the empty-cart fallback into the api mode (adding the
 * first floor from a product page) kept a container with no section id. The
 * pipeline then had nothing to fetch with and "You may also like" stayed empty
 * until a reload rendered the container in the right mode.
 */
const API_HOOKS = [
  "cart-recommendations",
  "data-cart-recommendations",
  "data-cart-recommendations-section",
  "data-cart-recommendations-container",
  "data-section-id",
];

function syncApiHooks(target, fresh) {
  if (!target || !fresh) return;

  const isApi = fresh.hasAttribute("data-cart-recommendations-section");

  API_HOOKS.forEach((name) => {
    if (isApi && fresh.hasAttribute(name)) {
      target.setAttribute(name, fresh.getAttribute(name));
    } else {
      target.removeAttribute(name);
    }
  });

  // The modal asks for more than the drawer section does; keep its own limit.
  if (isApi) {
    target.setAttribute("data-limit", target.getAttribute("data-limit") || "8");
  } else {
    target.removeAttribute("data-limit");
  }
}

/** The section id is stamped on the markup so a refresh can ask for it again. */
function sectionIdFrom(root) {
  return root.querySelector("[data-accessory-section]")?.dataset.accessorySection || "";
}

/**
 * Re-render the section against the current cart.
 *
 * This runs in every mode. Standing down in any one of them stranded the modal
 * there: the basket moves between empty, with-accessories and without, and only
 * a fresh render knows which of the three it is now. The Shopify
 * recommendations pipeline fills its own slider afterwards, on the hooks the
 * refreshed markup brings with it.
 */
async function refresh() {
  const sectionId = sectionIdFrom(document);
  if (!sectionId) return false;

  let markup = "";
  try {
    // From /cart: the cards only render in the cart context
    const root = window.Shopify?.routes?.root || "/";
    const response = await fetch(`${root}cart?section_id=${encodeURIComponent(sectionId)}`, {
      headers: { Accept: "text/html" },
    });
    if (!response.ok) return false;
    markup = await response.text();
  } catch (error) {
    console.warn("cart-accessories: section refresh failed", error);
    return false;
  }

  const wrapper = document.createElement("div");
  wrapper.innerHTML = markup;
  const parsed = wrapper.querySelector("template")?.content || wrapper;
  const fresh = parsed.querySelector(CONTAINER);
  if (!fresh) {
    // Loud rather than silent: a refresh that quietly does nothing leaves the
    // modal showing whatever the basket used to be, which reads as the wrong
    // mode having been chosen rather than as a failed fetch.
    console.warn("cart-accessories: section markup had no recommendations container", sectionId);
    return false;
  }

  // The template is what every open clones from, so it is refreshed whether or
  // not the modal happens to be on screen right now.
  const template = document.querySelector("#cart-recs-modal-template");
  const templateTarget = template?.content?.querySelector(".cart-recommendations");
  if (templateTarget) {
    templateTarget.innerHTML = fresh.innerHTML;
    syncApiHooks(templateTarget, fresh);
  }

  const live = document.querySelector(`${MODAL} ${CONTAINER}`);
  if (live) {
    live.innerHTML = fresh.innerHTML;
    syncApiHooks(live, fresh);
    fillGroups(live);
    ensureSliders();
  }

  /**
   * Shopify's recommendations come from their own pipeline, and it decides
   * whether to run by looking at the markup on the page when the cart event
   * fires - which is the markup for the basket the shopper just left. Going
   * from a basket with accessories to one without, it saw markup marked static,
   * stood down, and never came back to fill the slider this refresh had since
   * put in its place. Asking it again once the new markup is in is what makes
   * "You may also like" populate.
   */
  const mode = fresh.querySelector("[data-accessory-mode]")?.dataset.accessoryMode || "";
  if (mode === "api") await fillApiSlider();

  return true;
}

async function getController() {
  if (window.themeBlazeSliderController) return window.themeBlazeSliderController;
  try {
    const mod = await import("@theme/blaze-slider-controller");
    return mod?.blazeSliderController || null;
  } catch (error) {
    console.warn("cart-accessories: slider controller unavailable", error);
    return null;
  }
}

/**
 * These groups init their own sliders rather than waiting to be picked up.
 *
 * The theme's global slider watcher runs at DOM ready, and the modal's markup
 * is inside a <template> at that point - template content is a separate
 * fragment, so a querySelector over the document never sees it. That leaves
 * cart-recs-sliders.js as the only thing binding them, and it stands down for
 * markup it considers static. The result was uninitialised sliders: no drag,
 * no arrows, every card at full width.
 *
 * The controller keeps its own record of what it has initialised, so anything
 * not currently marked as initialised is destroyed first to clear that record -
 * the same dance cart-recs-sliders.js does after a re-render.
 */
async function ensureSliders() {
  const pending = [...document.querySelectorAll(SLIDERS)].filter((slider) => {
    if (slider.getAttribute("data-slider-initialized") === "true") return false;

    // Never initialise inside a modal that is still hidden. Blaze measures the
    // track when it binds, and in a display:none panel every width reads zero -
    // it decides there is nothing to scroll, marks itself static, and disables
    // the arrows for good. cart-recs-sliders.js refuses for the same reason.
    const modal = slider.closest(MODAL);
    return !modal || modal.getAttribute("aria-hidden") !== "true";
  });
  if (!pending.length) return;

  const controller = await getController();
  if (!controller?.init) return;

  pending.forEach((slider) => {
    try {
      controller.destroy?.(slider);
    } catch {
      /* nothing to destroy */
    }

    // One slider at a time, never controller.init(root). init() maps over every
    // [data-blaze-slider] beneath its root, and for one already running that
    // means refresh() - which has Blaze rebuild the pagination buttons. Anything
    // another module had put on those buttons goes with them, and the hero
    // thumbnails are exactly that: hero-thumbnails.js sets --hero-thumb on each
    // one, marks the slider done, and never comes back. Passing document here
    // stripped the hero and PDP gallery thumbnails on every page this ran on.
    controller.initSlider?.(slider);
  });
}

function onDomReady(callback) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback, { once: true });
  } else {
    callback();
  }
}

let apiFillPending = false;

/**
 * Shopify's recommendations need the section hooks to be in the document, and
 * getRecommendationContext looks for them with a plain querySelector. Until the
 * modal is mounted those hooks are inside its <template>, which that search
 * cannot see - so on mobile, where the modal only mounts when the shopper taps
 * the prompt, a cart change left the pipeline with no section id to fetch with
 * and "You may also like" opened empty.
 *
 * Rather than guess at when it mounts, this asks the question the other way
 * round: is a visible modal showing the recommendations mode with nothing in
 * the track? Then it still needs filling.
 */
function apiSliderNeedsFilling() {
  const live = document.querySelector(`${MODAL} ${CONTAINER}`);
  if (!live || live.closest(MODAL)?.getAttribute("aria-hidden") === "true") return false;
  if (live.querySelector("[data-accessory-mode]")?.dataset.accessoryMode !== "api") return false;

  return !live.querySelector(".blaze-track > *");
}

async function fillApiSlider() {
  if (apiFillPending || !apiSliderNeedsFilling()) return;

  apiFillPending = true;
  try {
    await window.cartRecommendationsModal?.reloadRecommendations?.();
  } catch (error) {
    console.warn("cart-accessories: recommendations reload failed", error);
  } finally {
    apiFillPending = false;
  }
}

let modalWatched = false;

/**
 * The modal opening is the moment everything here becomes possible: the sliders
 * can finally be measured, and the recommendations pipeline can finally find
 * the section hooks in the document. On mobile that moment can be minutes after
 * the cart change that decided what the modal should show, which is too far
 * away for a poll started by the cart event to still be running.
 *
 * Narrow on purpose - one attribute on one element. The theme's slider code
 * warns against observing attributes broadly, because the controller mutates
 * them as it initialises and an observer that watches those feeds itself.
 * aria-hidden is only ever written by the modal controller.
 */
function watchModal() {
  if (modalWatched) return;

  const modal = document.querySelector(MODAL);
  if (!modal) return;

  modalWatched = true;
  new MutationObserver(() => {
    if (modal.getAttribute("aria-hidden") !== "true") hydrate();
  }).observe(modal, { attributes: true, attributeFilter: ["aria-hidden"] });
}

/** Fill the figures in, then make sure the sliders they sit in actually run. */
function hydrate(scope = document) {
  watchModal();
  fillGroups(scope);
  ensureSliders();
  fillApiSlider();
}

onDomReady(() => hydrate());

document.addEventListener("change", onQuantityChanged);

document.addEventListener("click", (event) => {
  const button = event.target.closest?.("[data-accessory-add-all-button]");
  if (button) addAll(button);
});

document.addEventListener("click", onQuickAddClick);

/**
 * A bounded poll rather than one delayed pass. The modal mounts, then opens,
 * then paints, and only after that can its sliders be measured - a single
 * timeout landed in the middle of that far too often. Stops as soon as every
 * slider is bound, or gives up after a few seconds.
 *
 * Safe to run this often: fillGroups skips cards it has already written and
 * cards the shopper has set themselves.
 */
function scheduleHydrate() {
  let attempts = 0;
  const timer = window.setInterval(() => {
    hydrate();

    // Not just "are the sliders bound" - on mobile the modal is not even
    // mounted on the first tick, so that alone reported done before there was
    // anything to do.
    const pending = document.querySelector(`${SLIDERS}:not([data-slider-initialized="true"])`);
    const settled = !pending && !apiSliderNeedsFilling();
    if (settled || (attempts += 1) > 20) window.clearInterval(timer);
  }, 150);
}

// Clicks inside the modal must not restart the poll: re-initialising a slider
// resets it to the first slide, so an arrow click would advance and snap back.
document.addEventListener("click", (event) => {
  if (event.target.closest?.(MODAL)) return;
  scheduleHydrate();
});

["cart:updated", "cart:refresh", "cart:sections-rendered"].forEach((name) => {
  window.addEventListener(name, () => {
    // Closing first: the refresh replaces the markup underneath, and a modal
    // put away before that happens never shows the swap.
    onCartChanged();

    refresh().then((replaced) => {
      if (!replaced) hydrate();
    });
  });
});

document.addEventListener("shopify:section:load", (event) => hydrate(event?.target || document));

export { hydrate as initCartAccessories };
export default hydrate;
